import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { VenteEvolutionDialogData, VenteEvolutionFactureRow } from "./stats-vente-evolution-dialog.component";

const C = {
  headerBg: "FF1B2A4A", headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3", groupFont: "FF1B2A4A",
  subBg: "FFF5F7FA", border: "FFD0D5DD",
  statGreen: "FFE6FFF5", statPurple: "FFF0E6FF", statOrange: "FFFFF0E0",
  titleColor: "FF1B2A4A",
  margeGood: "FF16A34A", margeWarn: "FFF59E0B", margeBad: "FFDC2626",
};

function colLetter(col: number): string {
  let s = "", c = col;
  while (c > 0) { c--; s = String.fromCharCode(65 + (c % 26)) + s; c = Math.floor(c / 26); }
  return s;
}
function cellRef(col: number, row: number): string { return `${colLetter(col)}${row}`; }

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}
function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

// ─────────────────────────────────────────────────────────────────────────────
// Feuille "Par facture" : Facture | Date | Client | Lot | Qté UV | PU vente | Coût U | Marge € | Taux
// ─────────────────────────────────────────────────────────────────────────────

function buildFactureSheet(wb: ExcelJS.Workbook, data: VenteEvolutionDialogData) {
  const ws = wb.addWorksheet("Par facture", { views: [{ state: "frozen", ySplit: 5 }] });

  const COL = { numero: 1, date: 2, client: 3, lot: 4, qty: 5, puVente: 6, puCout: 7, marge: 8, taux: 9 };
  [18, 12, 24, 18, 10, 12, 12, 14, 10].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // Title
  ws.mergeCells("A1:H1");
  const tc = ws.getCell("A1");
  tc.value = `Évolution prix de vente — ${data.articleDenomination} (${data.tiersDenomination})`;
  tc.font = { bold: true, size: 14, color: { argb: C.titleColor } };
  ws.getRow(1).height = 26;

  // Stats
  const totalCA = data.factures.reduce((s, f) => s + f.ca, 0);
  const totalCout = data.factures.reduce((s, f) => s + f.cout, 0);
  const totalMarge = totalCA - totalCout;
  const tauxMarge = totalCA > 0 ? (totalMarge / totalCA) * 100 : 0;
  const totalQty = data.factures.reduce((s, f) => s + f.qtyUV, 0);
  const puMoyen = totalQty !== 0 ? totalCA / totalQty : 0;

  ws.mergeCells("A3:C3"); ws.mergeCells("D3:F3"); ws.mergeCells("G3:I3");
  const s1 = ws.getCell("A3");
  s1.value = `PU moyen : ${puMoyen.toFixed(2)} € / ${data.articleUniteVente || "UV"}`;
  statStyle(s1, C.statPurple);
  const s2 = ws.getCell("D3");
  s2.value = `CA : ${fmtEur(totalCA)} · Coût : ${fmtEur(totalCout)}`;
  statStyle(s2, C.statOrange);
  const s3 = ws.getCell("G3");
  s3.value = `Marge : ${fmtEur(totalMarge)} (${tauxMarge.toFixed(1)}%)`;
  statStyle(s3, tauxMarge >= 35 ? C.statGreen : tauxMarge >= 20 ? C.statOrange : "FFFEE2E2");
  ws.getRow(3).height = 26;

  // Headers
  const headers = ["N° Facture", "Date", "Client", "Lot", "Qté UV", "PU vente (€)", "Coût U. (€)", "Marge (€)", "Taux"];
  headers.forEach((h, i) => { const c = ws.getCell(5, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(5).height = 22;
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 9 } };

  const eurFmt = '#,##0.00\\ "€"';
  let row = 6;
  const factureRowNumbers: number[] = [];

  // Ordre chronologique desc pour lisibilité business
  const sortedFactures = [...data.factures].sort((a, b) =>
    a.dateKey === b.dateKey ? a.numero.localeCompare(b.numero) : b.dateKey.localeCompare(a.dateKey),
  );

  for (const f of sortedFactures) {
    const factRow = row;
    factureRowNumbers.push(factRow);

    // Ligne "facture" agrégée
    const gr = ws.getRow(factRow);
    gr.getCell(COL.numero).value = f.numero;
    gr.getCell(COL.date).value = f.dateKey ? new Date(f.dateKey) : "";
    gr.getCell(COL.date).numFmt = "dd/mm/yyyy";
    gr.getCell(COL.client).value = f.clientDenomination;
    gr.getCell(COL.lot).value = `${f.lots.length} lot${f.lots.length > 1 ? "s" : ""}`;
    gr.getCell(COL.qty).value = f.qtyUV;
    gr.getCell(COL.puVente).value = f.puVenteMoyen;
    gr.getCell(COL.puCout).value = f.puCoutMoyen;
    gr.getCell(COL.marge).value = f.marge;
    gr.getCell(COL.taux).value = { formula: `IF(${cellRef(COL.puVente, factRow)}=0,0,1-${cellRef(COL.puCout, factRow)}/${cellRef(COL.puVente, factRow)})`, result: f.tauxMarge / 100 } as any;

    for (let c = 1; c <= 9; c++) {
      gr.getCell(c).font = { bold: true, size: 10, color: { argb: C.groupFont } };
      gr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
      gr.getCell(c).border = { bottom: { style: "thin", color: { argb: C.border } }, top: { style: "thin", color: { argb: C.border } } };
    }
    [COL.puVente, COL.puCout, COL.marge].forEach((c) => (gr.getCell(c).numFmt = eurFmt));
    gr.getCell(COL.qty).numFmt = "#,##0";
    gr.getCell(COL.taux).numFmt = "0.0%";
    row++;

    // Sous-lignes par lot
    for (const lot of f.lots) {
      const sr = ws.getRow(row);
      sr.getCell(COL.lot).value = lot.lotNumero || "— inconnu —";
      sr.getCell(COL.qty).value = lot.qtyUV;
      sr.getCell(COL.puVente).value = lot.puVente;
      sr.getCell(COL.puCout).value = lot.puCout;
      sr.getCell(COL.marge).value = lot.marge;
      sr.getCell(COL.taux).value = { formula: `IF(${cellRef(COL.puVente, row)}=0,0,1-${cellRef(COL.puCout, row)}/${cellRef(COL.puVente, row)})`, result: lot.tauxMarge / 100 } as any;

      for (let c = 1; c <= 9; c++) {
        sr.getCell(c).font = { size: 9, italic: true };
        sr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.subBg } };
        sr.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
      }
      [COL.puVente, COL.puCout, COL.marge].forEach((c) => (sr.getCell(c).numFmt = eurFmt));
      sr.getCell(COL.qty).numFmt = "#,##0";
      sr.getCell(COL.taux).numFmt = "0.0%";
      row++;
    }
  }

  // Conditional formatting sur Taux
  if (row > 6) {
    ws.addConditionalFormatting({
      ref: `${colLetter(COL.taux)}6:${colLetter(COL.taux)}${row}`,
      rules: [
        { type: "cellIs", operator: "greaterThan" as any, formulae: ["0.349"], priority: 1, style: { font: { color: { argb: C.margeGood }, bold: true } } },
        { type: "cellIs", operator: "greaterThan" as any, formulae: ["0.199"], priority: 2, style: { font: { color: { argb: C.margeWarn }, bold: true } } },
        { type: "cellIs", operator: "lessThan" as any, formulae: ["0.20"], priority: 3, style: { font: { color: { argb: C.margeBad }, bold: true } } },
      ],
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Feuille "Par mois" : Mois | Nb factures | Qté UV | PU moyen | CA | Coût | Marge | Taux
// ─────────────────────────────────────────────────────────────────────────────

function buildMonthSheet(wb: ExcelJS.Workbook, data: VenteEvolutionDialogData) {
  const ws = wb.addWorksheet("Par mois", { views: [{ state: "frozen", ySplit: 5 }] });

  const COL = { mois: 1, nb: 2, qty: 3, pu: 4, ca: 5, cout: 6, marge: 7, taux: 8 };
  [18, 12, 12, 14, 14, 14, 14, 10].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // Title
  ws.mergeCells("A1:G1");
  const tc = ws.getCell("A1");
  tc.value = `Évolution prix par mois — ${data.articleDenomination}`;
  tc.font = { bold: true, size: 14, color: { argb: C.titleColor } };
  ws.getRow(1).height = 26;

  // Aggregation par mois
  const map = new Map<string, { qty: number; ca: number; cout: number; nb: number }>();
  for (const f of data.factures) {
    const d = new Date(f.dateKey);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!map.has(key)) map.set(key, { qty: 0, ca: 0, cout: 0, nb: 0 });
    const m = map.get(key)!;
    m.qty += f.qtyUV;
    m.ca += f.ca;
    m.cout += f.cout;
    m.nb++;
  }
  const rows = [...map.entries()]
    .map(([key, v]) => ({
      key,
      nb: v.nb,
      qty: v.qty,
      pu: v.qty !== 0 ? v.ca / v.qty : 0,
      ca: v.ca,
      cout: v.cout,
      marge: v.ca - v.cout,
    }))
    .sort((a, b) => b.key.localeCompare(a.key));

  // Headers
  const headers = ["Mois", "Nb factures", "Qté UV", "PU moyen (€)", "CA (€)", "Coût (€)", "Marge (€)", "Taux"];
  headers.forEach((h, i) => { const c = ws.getCell(5, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(5).height = 22;
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: 8 } };

  const eurFmt = '#,##0.00\\ "€"';
  let row = 6;

  for (const m of rows) {
    const r = ws.getRow(row);
    r.getCell(COL.mois).value = m.key;
    r.getCell(COL.nb).value = m.nb;
    r.getCell(COL.qty).value = m.qty;
    r.getCell(COL.pu).value = m.pu;
    r.getCell(COL.ca).value = m.ca;
    r.getCell(COL.cout).value = m.cout;
    r.getCell(COL.marge).value = { formula: `${cellRef(COL.ca, row)}-${cellRef(COL.cout, row)}`, result: m.marge } as any;
    r.getCell(COL.taux).value = { formula: `IF(${cellRef(COL.ca, row)}=0,0,${cellRef(COL.marge, row)}/${cellRef(COL.ca, row)})`, result: m.ca > 0 ? m.marge / m.ca : 0 } as any;

    for (let c = 1; c <= 8; c++) {
      r.getCell(c).font = { size: 10 };
      r.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
    }
    r.getCell(COL.mois).font = { bold: true, size: 10, color: { argb: C.groupFont } };
    [COL.pu, COL.ca, COL.cout, COL.marge].forEach((c) => (r.getCell(c).numFmt = eurFmt));
    r.getCell(COL.qty).numFmt = "#,##0";
    r.getCell(COL.nb).numFmt = "0";
    r.getCell(COL.taux).numFmt = "0.0%";
    row++;
  }

  if (row > 6) {
    ws.addConditionalFormatting({
      ref: `${colLetter(COL.taux)}6:${colLetter(COL.taux)}${row}`,
      rules: [
        { type: "cellIs", operator: "greaterThan" as any, formulae: ["0.349"], priority: 1, style: { font: { color: { argb: C.margeGood }, bold: true } } },
        { type: "cellIs", operator: "greaterThan" as any, formulae: ["0.199"], priority: 2, style: { font: { color: { argb: C.margeWarn }, bold: true } } },
        { type: "cellIs", operator: "lessThan" as any, formulae: ["0.20"], priority: 3, style: { font: { color: { argb: C.margeBad }, bold: true } } },
      ],
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export async function exportVenteEvolutionExcel(
  data: VenteEvolutionDialogData,
  ncs?: import("../../../non-conformite/models/non-conformite.model").NonConformite[],
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  buildFactureSheet(wb, data);
  await addJustupBranding(wb, wb.getWorksheet("Par facture")!, 9);

  buildMonthSheet(wb, data);
  await addJustupBranding(wb, wb.getWorksheet("Par mois")!, 8);

  if (ncs && ncs.length > 0) {
    const { addNcSheet } = await import("../../../non-conformite/utils/nc-excel-sheet.utils");
    addNcSheet(wb, ncs, {
      contextLabel: `${data.tiersDenomination} · ${data.articleDenomination}`,
    });
  }

  const buffer = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const slug = (data.articleDenomination || "article").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
  const filename = `evolution-prix-vente-${slug}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 10000);
}
